import * as React from 'react';

import {
  Button,
  ComboBox,
  type ComboBoxOption,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  useComboBoxOptions,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { $setFileAsTemplate } from '../../_state/fileActions.state';
import { $templateDialogFileId } from '../../_state/fileDialogs.state';
import { $files } from '../../_state/files.state';
import { useTranslations } from '../../i18n/useTranslations';

import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_TYPE_TO_SETTING_ID,
} from '~/modules/settings/_state/locationDocumentTemplate.state';
import { useIdentity } from '~/shared/hooks';

type SortableOption = ComboBoxOption<string> & { sortKey: number };

const documentTypeOptions: SortableOption[] = TEMPLATE_CATEGORIES.flatMap(
  (cat, catIndex) =>
    cat.types.map((t, typeIndex) => ({
      id: TEMPLATE_TYPE_TO_SETTING_ID[t.key],
      label: t.label,
      subLabel: cat.category,
      sortKey: catIndex * 100 + typeIndex,
    })),
);

const sortByCategory = <T extends { id: string }>(options: T[]): T[] => {
  const orderMap = new Map(documentTypeOptions.map((o) => [o.id, o.sortKey]));
  return [...options].sort(
    (a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999),
  );
};

export const FileSetTemplateDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [fileId, setFileId] = useAtom($templateDialogFileId);
  const files = useAtomValue($files);
  const isOpen = fileId !== null;
  const { organization } = useIdentity();

  const [selectedDocType, setSelectedDocType] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const patchTemplateSetting = useSetAtom($setFileAsTemplate);

  const { query, onChangeQuery, filteredOptions } = useComboBoxOptions({
    options: documentTypeOptions as { id: string; label: string }[],
    value: selectedDocType[0] ?? '',
  });

  const selectedFile = files.find((f) => f.id === fileId);

  const handleClose = () => {
    setFileId(null);
    setSelectedDocType([]);
  };

  const selectedSettingId = selectedDocType[0];

  const handleSubmit = async () => {
    if (!selectedSettingId || !fileId || !organization) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (!organization) return;

      await patchTemplateSetting({
        organizationId: organization.id,
        settingId: selectedSettingId,
        fileId,
      });

      toast.success(t.templateDialog.successUpdated);
      handleClose();
    } catch (error) {
      toast.error(t.templateDialog.failedToUpdate);
      console.error('Error updating template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !fileId || !selectedFile) {
    return null;
  }

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      className="@FileSetTemplateDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t.templateDialog.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <div className="text-sm text-neutral/60">
            {t.templateDialog.settingAs.replace('{name}', selectedFile.name)}
          </div>

          <ComboBox
            label={t.templateDialog.documentType}
            placeholder={t.templateDialog.selectDocumentType}
            options={
              sortByCategory(filteredOptions) as ComboBoxOption<string>[]
            }
            value={selectedDocType}
            onChange={setSelectedDocType}
            query={query}
            onChangeQuery={onChangeQuery}
            getSelection={(ids) =>
              ids.map((id) => {
                const opt = documentTypeOptions.find((o) => o.id === id);
                return { label: opt?.label ?? '', subLabel: opt?.subLabel };
              })
            }
            isRequired
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.templateDialog.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleSubmit}
            isDisabled={!selectedSettingId || isSubmitting}
            isLoading={isSubmitting}
          >
            {t.templateDialog.setAsTemplate}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
